import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AppModalService } from '../../../core/services/app-modal.service';
import { ExistingIssueContent } from '../../../components/Issues-Tab/existing-issue/existing-issue-content/existing-issue-content';
import { AppIssuesService } from '../../../core/services/issues.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-edit-existing-issue',
  standalone: true,
  imports: [ExistingIssueContent],
  templateUrl: './app-edit-existing-issue.html',
  styleUrl: './app-edit-existing-issue.scss',
})
export class AppEditExistingIssue implements OnInit {
  @ViewChild('existingIssueEditModal')
  existingIssueEditModal!: ElementRef<HTMLElement>;
  @ViewChild('existingIssueContent')
  existingIssueContent!: ExistingIssueContent;
  isExistingIssueEditModalVisible = false;
  title = '';
  issuePositionX = 0;
  issuePositionY = 0;
  existingIssueForm!: FormGroup;
  editTableData: any;

  private readonly appModalService = inject(AppModalService);
  private readonly issuesService = inject(AppIssuesService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.existingIssueForm = this.issuesService.existingIssueForm;
    // Subscribe to the existing issue edit state to open the modal when an edit action is triggered.
    this.appModalService.existingIssueEditState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (!data) {
          return;
        }
        this.mapFormData(data);
        this.title = data.modalName ?? 'Update Issue';
        this.isExistingIssueEditModalVisible = true;
        setTimeout(() => {
          this.centerModal();
        });
      });

    // Subscribe to the all data state to keep the edit table data updated.
    this.appModalService.existingIssueAllDataState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data) {
          this.editTableData = data;
        }
      });

    this.registerExternalRefresh();
  }

  // Maps the incoming data to the form controls and initializes the modal with the relevant data.
  private mapFormData(data: any): void {
    const record = data?.rowData ?? {};
    const issueInfo = record.issueInfo?.[0] ?? {};
    const auditAfter2017 = record.auditAfter2017?.[0] ?? {};
    const auditPrior2018 = record.auditPrior2018?.[0] ?? {};

    this.existingIssueForm.reset(
      {
        id: issueInfo.id ?? 0,
        issueId: issueInfo.issueID ?? '',
        description: issueInfo.description ?? '',
        entityCode: issueInfo.entityCode ?? '',
        issueYear: issueInfo.issueYear ?? null,
        contact: issueInfo.contact ?? '',
        approver: issueInfo.approver ?? '',
        cfcNc902: issueInfo.cfc ?? '',
        epAdjRequired: auditAfter2017.isEPAdjRequired ?? null,
        tbbsAdjRequired: auditAfter2017.isTBBSAdjRequired ?? null,
        dateReviewed: issueInfo.dateReviewed ? this.formatDate(issueInfo.dateReviewed) : null,
        dateCorptaxAdj: issueInfo.dateCorptaxAdj ? this.formatDate(issueInfo.dateCorptaxAdj) : null,
        adjDescrip: issueInfo.desc ?? '',
        arFilingId: issueInfo.amendedID ?? null,
        arIssueId: issueInfo.arIssueID ?? null,
        irsIssueID: issueInfo.irsIssueID ?? null,
        saa: issueInfo.saA_ID ?? null,
        submission: auditAfter2017.saaSubmission ?? null,
        spDocLink: issueInfo.sP_DocID ?? null,
        npa: issueInfo.npa ?? null,
        npaTiesToArSaa: auditPrior2018.isNPATied ?? null,
        issueStatus: issueInfo.issue_Status ?? null,
        deduction199: issueInfo.deduction199 ?? false,
        creationDate: issueInfo.dateCreation ?? null,
        modificationDate: issueInfo.dateModification ?? null,
      },
      {
        emitEvent: false,
      },
    );

    this.existingIssueForm.markAsPristine();
    this.existingIssueForm.markAsUntouched();

    // Open the modal with all related data for the existing issue.
    this.appModalService.openExistingIssueAllData({
      incomeCredit2018: record.prior2018 ?? [],
      auditChanges2018: record.auditPrior2018 ?? [],
      incomeCreditAfter2017: record.after2017 ?? [],
      auditChangesAfter2017: record.auditAfter2017 ?? [],
    });
  }

  // Register a subscription to listen for external refresh actions related to the existing issue edit modal.
  private registerExternalRefresh(): void {
    this.issuesService.reloadIssueDetail$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((actionName: string) => {
        if (actionName === 'refresh-edit') {
          this.isExistingIssueEditModalVisible = false;
        }
      });
  }

  // Format a date string to 'YYYY-MM-DD' format, or return null if the input is invalid.
  private formatDate(date: string | null): string | null {
    if (!date) {
      return null;
    }
    return date.split('T')[0];
  }

  // Save the current record in the existing issue edit modal.
  saveRecord(): void {
    this.existingIssueContent.saveRecord(true);
  }

  // Close the existing issue edit modal.
  close(): void {
    this.isExistingIssueEditModalVisible = false;
    this.appModalService.updateEditIssueStatus(false);
  }

  // Center the existing issue edit modal on the screen.
  private centerModal(): void {
    if (!this.existingIssueEditModal) {
      return;
    }
    const pos = this.appModalService.centerModal(this.existingIssueEditModal.nativeElement);
    this.issuePositionX = pos.positionX;
    this.issuePositionY = pos.positionY;
  }

  // Initiate dragging of the existing issue edit modal.
  startIssueDrag(event: MouseEvent): void {
    if (!this.existingIssueEditModal) {
      return;
    }
    this.appModalService.startDrag(
      event,
      this.issuePositionX,
      this.issuePositionY,
      this.existingIssueEditModal.nativeElement,
    );
  }

  // Handle mouse move events for dragging the existing issue edit modal.
  @HostListener('document:mousemove', ['$event'])
  onMouseMoveI(event: MouseEvent): void {
    if (!this.existingIssueEditModal) {
      return;
    }
    const pos = this.appModalService.drag(event, this.existingIssueEditModal.nativeElement);
    if (!pos) {
      return;
    }
    this.issuePositionX = pos.positionX;
    this.issuePositionY = pos.positionY;
  }

  // Handle mouse up events to stop dragging the existing issue edit modal.
  @HostListener('document:mouseup')
  onMouseUpI(): void {
    this.appModalService.stopDrag();
  }
}
